import type { EventSourceManager } from './eventSourceManager';
import type {
  AutoPricingMessage,
  IAutoPricingService,
  AutoPricingStatus,
} from './types';

type AutoPricingEvent = {
  hash: string;
  data: { status: AutoPricingStatus };
};

export class AutoPricingService implements IAutoPricingService {
  _eventSourceManager: EventSourceManager;
  _baseURL: string;

  constructor(eventSourceManager: EventSourceManager, baseURL: string) {
    this._eventSourceManager = eventSourceManager;
    this._baseURL = baseURL;
  }

  init(carleadHash: string) {
    this._eventSourceManager.init(
      `${this._baseURL}/self-evaluation-client-notifications/autopricing/${carleadHash}`,
    );
  }

  get isInitialized() {
    return this._eventSourceManager.isInitialized;
  }

  #createMessageHandler(
    onMessage: (value: AutoPricingMessage) => any,
    onError: (error: any) => any,
    options: { single?: boolean },
  ) {
    const handler = (event: MessageEvent<string>) => {
      if (options.single) {
        this._eventSourceManager.unsubscribe(handler);
      }

      try {
        const eventData: AutoPricingEvent = JSON.parse(event.data);
        onMessage({ status: eventData.data.status });
      } catch (err) {
        onError(err);
      }
    };

    return handler;
  }

  listenOnce() {
    return new Promise<AutoPricingMessage>((resolve, reject) => {
      this.listen(resolve, reject, { single: true });
    });
  }

  listen(
    onMessage: (value: AutoPricingMessage) => any,
    onError: (error: Event) => any,
    options: { single?: boolean } = {},
  ) {
    const handler = this.#createMessageHandler(onMessage, onError, options);
    const errorHandler = (err: Event) => {
      this._eventSourceManager.unsubscribe(handler);
      this._eventSourceManager.removeErrorHandler(errorHandler);
      onError(err);
    };

    this._eventSourceManager.addErrorHandler(errorHandler);
    this._eventSourceManager.subscribe(handler);

    return () => {
      this._eventSourceManager.unsubscribe(handler);
      this._eventSourceManager.removeErrorHandler(errorHandler);
    };
  }

  closeConnection() {
    this._eventSourceManager.close();
  }
}
